import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate, formatTime } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';

const statusVariants: Record<string, 'sage' | 'sand' | 'terracotta' | 'warning' | 'error'> = {
  pending: 'warning',
  confirmed: 'sage',
  checked_in: 'sage',
  completed: 'sand',
  cancelled: 'error',
  no_show: 'error',
};

export function BookingsPage() {
  const { data: bookings, isLoading } = useQuery({
    queryKey: ['bookings'],
    queryFn: () => api.bookings.list({ pageSize: 50 }),
  });

  return (
    <div className="bg-sand-50 min-h-screen py-8">
      <div className="container-wide">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-sand-900">My Bookings</h1>
            <p className="mt-2 text-sand-500">View and manage your tee time reservations</p>
          </div>
          <Link to="/tee-times">
            <Button variant="sage">Book New Tee Time</Button>
          </Link>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonCard key={i} className="h-32" />
            ))}
          </div>
        ) : bookings?.data?.length === 0 ? (
          <EmptyState
            icon={
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            title="No bookings yet"
            description="Book your first tee time to get started."
            action={
              <Link to="/tee-times">
                <Button variant="sage">Browse Tee Times</Button>
              </Link>
            }
          />
        ) : (
          <div className="space-y-4">
            {bookings?.data?.map((booking) => (
              <Link key={booking.id} to={`/bookings/${booking.id}`}>
                <Card variant="hover">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-sand-900">{booking.course?.name}</h3>
                          <p className="text-sm text-sand-500">
                            {booking.course?.city}, {booking.course?.state}
                          </p>
                        </div>
                        <Badge variant={statusVariants[booking.status] || 'sand'}>
                          {booking.status.replace('_', ' ')}
                        </Badge>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                        <div className="flex items-center gap-2 text-sand-600">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDate(booking.teeTime?.date, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="flex items-center gap-2 text-sand-600">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formatTime(booking.teeTime?.time)}
                        </div>
                        <div className="flex items-center gap-2 text-sand-600">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          {booking.playerCount} player{booking.playerCount !== 1 ? 's' : ''}
                        </div>
                        <div className="flex items-center gap-2 text-sand-600">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                          </svg>
                          {booking.confirmationCode}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <p className="text-lg font-semibold text-sage-600">
                        {formatCurrency(booking.totalAmount)}
                      </p>
                      <svg className="h-5 w-5 text-sand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
